import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'src/theming';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import { CreateTicketOptions } from 'src/interfaces/api/Ticket';
import { Controller, useForm } from 'react-hook-form';
import {
  CriticitySelect,
  DomainSelect,
  PrioritySelect,
  ProductTypeSelect,
  SupplierSelect,
  TicketTypeSelect,
} from 'src/components/ApiField';
import ContactField from './components/ContactField';
import EmployeeAutocomplete from 'src/components/ApiField/EmployeeAutocomplete';

const useStyles = createUseStyles({
  title: {
    width: 400,
    '& h2': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
  formControl: {
    marginBottom: '16px !important',
  },
});

interface GlobalTicketDialogProps {
  siteId?: undefined;
  legacyDealId?: undefined;
  isGlobalTicket: true;
}

interface NotGlobalTicketDialogProps {
  siteId: string;
  legacyDealId?: number;
  isGlobalTicket?: false;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onValidate: (ticketOptions: CreateTicketOptions) => void;
}

type CreateTicketDialogProps = Props & (GlobalTicketDialogProps | NotGlobalTicketDialogProps);

const CreateTicketDialog: React.FC<CreateTicketDialogProps> = ({
  open,
  legacyDealId,
  siteId,
  onClose,
  isGlobalTicket,
  onValidate,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | undefined>();
  const [newContactFormEnabled, setNewContactFormEnabled] = useState(false);
  const { watch, reset, control, getValues, handleSubmit } = useForm<CreateTicketOptions>({
    defaultValues: { legacyDealId },
  });

  const selectedDomainId = watch('domainId');

  useEffect(() => {
    reset({ legacyDealId });
  }, [legacyDealId]);

  useEffect(() => {
    if (newContactFormEnabled) {
      reset({ ...getValues(), associatedContactId: undefined });
    } else {
      reset({
        ...getValues(),
        newContactEmail: undefined,
        newContactLastname: undefined,
        newContactFirstname: undefined,
        newContactPhoneNumber: undefined,
      });
    }
  }, [newContactFormEnabled]);

  useEffect(() => {
    setFormError(undefined);
  }, [open]);

  const onSubmit = async (ticketOptions: CreateTicketOptions) => {
    if (!loading) {
      try {
        setLoading(true);
        setFormError(undefined);
        await onValidate(ticketOptions);
        setLoading(false);
        onClose();
      } catch (e) {
        setLoading(false);
        setFormError(e.message);
      }
    }
  };

  return (
    <Dialog onClose={onClose} scroll='paper' open={open}>
      <DialogTitle className={classes.title}>Créer un ticket {isGlobalTicket ? 'général' : ''}</DialogTitle>

      <DialogContent dividers>
        <form>
          <Controller
            name='domainId'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <DomainSelect
                {...field}
                className={classes.formControl}
                error={undefined !== error}
                helperText={error?.message}
                required
                fullWidth
              />
            )}
          />

          {!isGlobalTicket && (
            <>
              <Controller
                name='type'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field, fieldState: { error } }) => (
                  <TicketTypeSelect
                    {...field}
                    className={classes.formControl}
                    error={undefined !== error}
                    helperText={error?.message}
                    required
                  />
                )}
              />

              <Controller
                name='productType'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field, fieldState: { error } }) => (
                  <ProductTypeSelect
                    {...field}
                    domainId={selectedDomainId}
                    className={classes.formControl}
                    error={undefined !== error}
                    helperText={error?.message}
                    required
                  />
                )}
              />
            </>
          )}

          <Controller
            name='subject'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Sujet'
                fullWidth
                variant='outlined'
                error={undefined !== error}
                helperText={error?.message}
                required
              />
            )}
          />

          <Controller
            name='supplierOfProductId'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <SupplierSelect
                {...field}
                domainId={selectedDomainId}
                className={classes.formControl}
                error={undefined !== error}
                helperText={error?.message}
                required
              />
            )}
          />

          {!isGlobalTicket && siteId && (
            <ContactField
              newContactFormEnabled={newContactFormEnabled}
              onSwitchNewContactForm={(enabled) => setNewContactFormEnabled(enabled)}
              siteId={siteId}
              control={control}
              className={classes.formControl}
            />
          )}

          <Controller
            name='criticity'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <CriticitySelect
                {...field}
                className={classes.formControl}
                error={undefined !== error}
                helperText={error?.message}
                fullWidth
                required
              />
            )}
          />

          <Controller
            name='priority'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <PrioritySelect
                {...field}
                className={classes.formControl}
                error={undefined !== error}
                helperText={error?.message}
                fullWidth
                required
              />
            )}
          />

          <Controller
            name='comment'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Commentaire'
                fullWidth
                multiline
                minRows={4}
                variant='outlined'
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          {!isGlobalTicket && (
            <Controller
              name='supportEmployeeId'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <EmployeeAutocomplete
                  {...field}
                  className={classes.formControl}
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          )}
        </form>
      </DialogContent>

      <DialogActions>
        <Button type='button' onClick={onClose} color='primary'>
          Annuler
        </Button>

        <Button type='submit' color='primary' disabled={loading} onClick={handleSubmit(onSubmit)}>
          {loading && <CircularProgress color='primary' size={15} />} Créer
        </Button>
      </DialogActions>

      {formError && <p className={classes.error}>{formError}</p>}
    </Dialog>
  );
};

export default CreateTicketDialog;
